import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CheckRounded,
  ErrorOutlineOutlined,
  FunctionsOutlined,
  PendingActionsOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette, detailCardSx } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { pfReconciliationHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { MilestoneStepper, PlainInput, RecordHeaderCard, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { ACCOUNTS, CODES, RECON_STATUS_COLORS, challanTotal, dueDate, formatDay, money, num, reconStatus } from "./pfReportsData";

// Reconciliation (P11), Record Challan (P11a) and Challan View (P11b): one
// row per PF code per month. Payable comes from the finalised payroll,
// Paid is the sum of the challans the company recorded; the status is
// worked out from the totals (B7.5).

const { green, pink } = detailAccentPalette;
const MONTHS = ["2026-10", "2026-09"];
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };

const sumAccounts = (challans) =>
  ACCOUNTS.reduce((result, account) => ({ ...result, [account.key]: challans.reduce((total, item) => total + (Number(item.amounts[account.key]) || 0), 0) }), {});

const AccountTable = ({ payable, paid, entry, onEntry }) => (
  <SimpleTable
    fit
    heads={[
      { label: "Account", width: 120 },
      { label: "Payable", align: "right", width: 90 },
      { label: onEntry ? "Paid before" : "Paid", align: "right", width: 90 },
      { label: onEntry ? "This challan" : "Balance", align: "right", width: 110 },
    ]}
  >
    {ACCOUNTS.map((account) => (
      <Mui.TableRow key={account.key}>
        <Mui.TableCell sx={tableCellSx}>{account.short}</Mui.TableCell>
        <Mui.TableCell align="right" sx={tableCellSx}>{num(payable[account.key])}</Mui.TableCell>
        <Mui.TableCell align="right" sx={tableCellSx}>{num(paid[account.key])}</Mui.TableCell>
        <Mui.TableCell align="right" sx={{ ...tableCellSx, py: onEntry ? 0.5 : undefined }}>
          {onEntry ? (
            <PlainInput
              type="number"
              value={entry[account.key]}
              onChange={(value) => onEntry(account.key, value)}
              inputProps={{ min: 0, "aria-label": `${account.short} this challan`, style: { textAlign: "right" } }}
              sx={{ width: 110 }}
            />
          ) : (
            <Mui.Box component="span" sx={{ fontWeight: designWeights.semibold }}>{num(payable[account.key] - paid[account.key])}</Mui.Box>
          )}
        </Mui.TableCell>
      </Mui.TableRow>
    ))}
    <Mui.TableRow>
      <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>Total</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>{num(payable.total)}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>{num(ACCOUNTS.reduce((total, account) => total + paid[account.key], 0))}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>
        {onEntry ? money(ACCOUNTS.reduce((total, account) => total + (Number(entry[account.key]) || 0), 0)) : num(payable.total - ACCOUNTS.reduce((total, account) => total + paid[account.key], 0))}
      </Mui.TableCell>
    </Mui.TableRow>
  </SimpleTable>
);

const ChallanList = ({ challans, title = "CHALLANS" }) =>
  challans.length ? (
    <RequestDetailsSection
      title={title}
      fields={challans.flatMap((challan) => [
        {
          icon: CheckRounded,
          label: `${challan.kind} challan · TRRN ${challan.trrn}`,
          value: money(challanTotal(challan)),
          helper: `Paid ${formatDay(challan.paid_on)}${challan.receipt ? " · receipt uploaded" : ""}`,
          accent: green,
        },
        ...(challan.receipt ? [{ icon: ReceiptLongOutlined, label: "Receipt", value: challan.receipt, helper: "View · Download", accent: pink }] : []),
      ])}
    />
  ) : null;

const RecordChallanPanel = ({ open, row, allChallans, onClose, onSave }) => {
  const [form, setForm] = useState({ kind: "Original", paid_on: "", trrn: "", receipt: "", remarks: "", amounts: {} });
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    const balance = Object.fromEntries(ACCOUNTS.map((account) => [account.key, String(Math.max(0, row.accounts[account.key] - row.paidAccounts[account.key]))]));
    setForm({ kind: row.challans.length ? "Supplementary" : "Original", paid_on: "", trrn: "", receipt: "", remarks: "", amounts: balance });
    setShowErrors(false);
  }, [open, row]);

  if (!row) return null;
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const thisTotal = ACCOUNTS.reduce((total, account) => total + (Number(form.amounts[account.key]) || 0), 0);
  const balanceAfter = row.payable - row.paid - thisTotal;
  const statusAfter = reconStatus(row.payable, row.paid + thisTotal);

  const errors = {};
  if (!form.paid_on) errors.paid_on = "Pick the paid date";
  if (!/^\d{13}$/.test(form.trrn)) errors.trrn = "TRRN is 13 digits";
  else if (allChallans.some((challan) => challan.trrn === form.trrn)) errors.trrn = "This TRRN is already recorded";
  if (!(thisTotal > 0)) errors.amounts = "Enter the amount paid";
  const error = (key) => (showErrors ? errors[key] : undefined);

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({
      code_id: row.code_id,
      month: row.month,
      kind: form.kind,
      trrn: form.trrn,
      paid_on: form.paid_on,
      receipt: form.receipt,
      remarks: form.remarks.trim(),
      amounts: Object.fromEntries(ACCOUNTS.map((account) => [account.key, Number(form.amounts[account.key]) || 0])),
    });
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Record Challan"
      subtitle={`${row.month_label} · ${row.code.type === "MAIN" ? "Main" : "Extension"} · ${row.code.code}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save Challan" primaryProps={{ onClick: save }} />}
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar="PF"
          title={`${row.code.type === "MAIN" ? "Main" : "Extension"} code · ${row.month_label}`}
          chip={<RequestStatusChip status={row.status} colorCode={RECON_STATUS_COLORS[row.status]} />}
          meta={`Payable ${money(row.payable)} · paid ${money(row.paid)}${row.paid && row.paid < row.payable ? ` · short ${money(row.payable - row.paid)}` : ""}`}
          tone={row.status === "Not recorded" ? "slate" : "warning"}
        />
        <ChallanList challans={row.challans} title="ALREADY RECORDED" />
        <FormSection title="New challan">
          <Field label={<>Challan type<Required /></>} helper={row.challans.length ? "Original already recorded" : undefined}>
            <SelectField
              value={form.kind}
              onChange={(value) => value && set("kind")(value)}
              options={[
                ...(row.challans.length ? [] : [{ value: "Original", label: "Original" }]),
                { value: "Supplementary", label: "Supplementary" },
              ]}
            />
          </Field>
          <Field label={<>Paid on<Required /></>} error={error("paid_on")}>
            <DateField value={form.paid_on} onChange={set("paid_on")} maxDate={moment().format("YYYY-MM-DD")} placeholder="Paid date" error={error("paid_on")} />
          </Field>
          <Field wide label={<>TRRN<Required /></>} helper="13 digits, shown when EPFO approves the return" error={error("trrn")}>
            <TextField value={form.trrn} onChange={(value) => set("trrn")(value.replace(/\D/g, "").slice(0, 13))} error={error("trrn")} placeholder="e.g. 2041611004120" />
          </Field>
          <Field wide label="Challan receipt (optional)">
            <Mui.Button
              component="label"
              variant="outlined"
              sx={{ width: "100%", py: 1.5, borderStyle: "dashed", textTransform: "none", color: designColors.tealDark, borderColor: designColors.teal, backgroundColor: designColors.tealTint }}
            >
              {form.receipt || "Upload challan receipt · PDF, JPG or PNG · max 5 MB"}
              <input
                hidden
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) set("receipt")(file.size > 5 * 1024 * 1024 ? "" : file.name);
                  event.target.value = "";
                }}
              />
            </Mui.Button>
          </Field>
          <Field wide label="Remarks" helper="Note any interest or damages paid on the same challan here.">
            <TextField value={form.remarks} onChange={set("remarks")} placeholder="Optional" />
          </Field>
        </FormSection>
        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>Amount by account</Mui.Typography>
          <AccountTable
            payable={row.accounts}
            paid={row.paidAccounts}
            entry={form.amounts}
            onEntry={(key, value) => setForm((current) => ({ ...current, amounts: { ...current.amounts, [key]: value } }))}
          />
          {error("amounts") ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 1 }}>{error("amounts")}</Mui.Typography> : null}
          <Mui.Typography sx={{ ...designType.captionText, mt: 1, color: statusAfter === "Paid" ? "#067647" : "#B54708", fontWeight: designWeights.semibold }}>
            {statusAfter === "Paid"
              ? "✓ Balance will be ₹0 — status will be Paid"
              : statusAfter === "Overpaid"
                ? `Paid ${money(-balanceAfter)} more than payable — status will be Overpaid`
                : `Balance will be ${money(balanceAfter)} — status will be ${statusAfter}`}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const ChallanViewPanel = ({ open, row, ecrGenerated, onClose, onSupplementary }) => {
  if (!row) return null;
  const reconciled = row.status === "Paid" && ACCOUNTS.every((account) => row.accounts[account.key] === row.paidAccounts[account.key]);
  const latest = row.challans[row.challans.length - 1];
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="PF Challan"
      subtitle={`${row.month_label} · ${row.code.type === "MAIN" ? "Main" : "Extension"} · ${row.code.code}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <SecondaryButton onClick={() => onSupplementary(row)}>Record supplementary</SecondaryButton>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar="PF"
          title={`${row.code.type === "MAIN" ? "Main" : "Extension"} code · ${row.month_label}`}
          chip={<RequestStatusChip status={row.status} colorCode={RECON_STATUS_COLORS[row.status]} />}
          meta={`${num(row.members)} members · payable ${money(row.payable)} · paid ${money(row.paid)}`}
          tone={row.status === "Paid" ? "teal" : "warning"}
        />
        <ChallanList challans={row.challans} />
        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>Amount by account</Mui.Typography>
          <AccountTable payable={row.accounts} paid={row.paidAccounts} />
        </Mui.Box>
        <MilestoneStepper
          title="Status"
          steps={[
            { label: "ECR generated", date: ecrGenerated ? formatDay(ecrGenerated) : "", done: Boolean(ecrGenerated) },
            { label: "Paid", date: latest ? formatDay(latest.paid_on) : "", done: row.paid > 0 },
            { label: "Reconciled", date: reconciled ? "Amounts match" : "", done: reconciled },
          ]}
        />
        {!reconciled && row.paid ? (
          <RequestInfoBanner tone="warning" body="Amounts don't match on every account yet. Record a supplementary challan for the balance." />
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PfReconciliationTab = ({ month, codeFilter, payableFor, register, challans, ecrFiles, onRecord }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState("all");
  const [panel, setPanel] = useState({ kind: null, key: null });
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_reconciliation_saved_views",
    fullHeaders: pfReconciliationHeader,
  });

  const allRows = useMemo(
    () =>
      MONTHS.flatMap((forMonth) =>
        CODES.filter((code) => codeFilter === "all" || String(code.id) === codeFilter).map((code) => {
          const accounts = payableFor(code.id, forMonth);
          const own = challans.filter((challan) => challan.code_id === code.id && challan.month === forMonth);
          const paidAccounts = sumAccounts(own);
          const paid = own.reduce((total, challan) => total + challanTotal(challan), 0);
          const status = reconStatus(accounts.total, paid);
          const latest = own[own.length - 1];
          return {
            id: `${forMonth}-${code.id}`,
            month: forMonth,
            month_label: moment(`${forMonth}-01`).format("MMM YYYY"),
            code,
            code_id: code.id,
            code_label: `${code.type === "MAIN" ? "Main" : "Extension"} · ${code.code}`,
            accounts,
            paidAccounts,
            payable: accounts.total,
            paid,
            trrn: latest?.trrn || "",
            paid_on: latest?.paid_on || "",
            status: status === "Partly paid" ? "Partly paid" : status,
            action: status === "Paid" || status === "Overpaid" ? "View" : "Record challan",
            challans: own,
            members: register.filter((row) => row.code_id === code.id).length,
          };
        }),
      ),
    [challans, codeFilter, payableFor, register],
  );

  const selected = (key) => allRows.find((row) => row.id === key) || null;
  const openRow = (row) => setPanel({ kind: row.action === "View" ? "view" : "record", key: row.id });
  const current = allRows.filter((row) => row.month === month);
  const rows =
    card === "paid" ? allRows.filter((row) => row.status === "Paid") : card === "none" ? allRows.filter((row) => row.status === "Not recorded") : card === "short" ? allRows.filter((row) => row.status === "Partly paid" || row.status === "Overpaid") : allRows;
  const monthLabel = moment(`${month}-01`).format("MMM");

  const renderCell = (row, key) => {
    switch (key) {
      case "payable":
        return num(row.payable);
      case "paid":
        return row.paid ? num(row.paid) : "—";
      case "paid_on":
        return row.paid_on ? formatDay(row.paid_on) : "—";
      case "status":
        return (
          <RequestStatusChip
            status={row.status === "Partly paid" ? `Partly paid · ${money(row.payable - row.paid)} short` : row.status}
            colorCode={RECON_STATUS_COLORS[row.status]}
          />
        );
      case "action":
        return <Mui.Box component="span" sx={linkSx}>{row.action}</Mui.Box>;
      default:
        return row[key] || "—";
    }
  };

  const recordRow = panel.kind === "record" ? selected(panel.key) : null;
  const viewRow = panel.kind === "view" ? selected(panel.key) : null;

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: "all", title: `Payable (${monthLabel})`, value: money(current.reduce((total, row) => total + row.payable, 0)), icon: FunctionsOutlined, tone: "blue" },
          { key: "paid", title: "Paid", value: money(current.reduce((total, row) => total + row.paid, 0)), icon: CheckRounded, tone: "green" },
          { key: "none", title: "Not recorded", value: money(current.filter((row) => row.status === "Not recorded").reduce((total, row) => total + row.payable, 0)), icon: PendingActionsOutlined, tone: "amber" },
          { key: "short", title: "Short or extra", value: allRows.filter((row) => row.status === "Partly paid" || row.status === "Overpaid").length, icon: ErrorOutlineOutlined, tone: "red" },
        ]}
      />
      <StatutoryList
        title="PF Reconciliation"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={openRow}
        selectedKey={panel.key}
        itemLabel="rows"
        searchPlaceholder="Search code or TRRN"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Mui.Typography sx={designType.captionText}>Due date: 15th of the next month ({monthLabel} challan due {dueDate(month).format("DD MMM YYYY")})</Mui.Typography>
        <RequestInfoBanner body="After paying on the EPFO portal, record the TRRN and challan here. Status is worked out from the amounts: Not recorded · Partly paid · Paid · Overpaid. If you paid short, record a supplementary challan for the balance." />
      </Mui.Stack>

      <RecordChallanPanel
        open={Boolean(recordRow)}
        row={recordRow}
        allChallans={challans}
        onClose={() => setPanel({ kind: null, key: null })}
        onSave={(challan) => {
          onRecord(challan);
          showToast("success", `Challan ${challan.trrn} recorded.`);
          setPanel({ kind: "view", key: panel.key });
        }}
      />
      <ChallanViewPanel
        open={Boolean(viewRow)}
        row={viewRow}
        ecrGenerated={viewRow ? ecrFiles.find((file) => file.code_id === viewRow.code_id && file.month === viewRow.month && file.status === "Current")?.generated_at : null}
        onClose={() => setPanel({ kind: null, key: null })}
        onSupplementary={(row) => setPanel({ kind: "record", key: row.id })}
      />
    </>
  );
};
